import dagre from 'dagre';
import { Node, Edge } from '@xyflow/react';
import { OrgChartNode } from '../../types';
import { OrgChartPersonNodeData } from './OrgChartPersonNode';
import { OrgChartJunctionNodeData } from './OrgChartJunctionNode';

const NODE_WIDTH = 200;
const NODE_HEIGHT = 96;
const JUNCTION_SIZE = 1;

// Configuration for the leaf node vertical stacking
const LEAF_STACK_CONFIG = {
  nodeVerticalSpacing: 100,    // Vertical spacing between stacked leaf nodes
  spineOffsetX: 10,            // Horizontal offset from leafGroup left edge to spine
  branchLength: 30,            // Length of horizontal branch to leaf node
  firstLeafOffsetY: 20,        // Vertical offset from leafGroup top to first junction
  minLeafChildrenForStack: 2,  // Minimum leaf children to apply stacking
  leafGroupPadding: 20,        // Extra padding around leaf group
  clientGroupGap: 32,          // Extra vertical gap between consecutive client groups
};

type AnyNodeData = OrgChartPersonNodeData | OrgChartJunctionNodeData;

interface GraphElements {
  nodes: Node<AnyNodeData>[];
  edges: Edge[];
}

interface LeafGroupInfo {
  parentId: string;
  leafChildIds: string[];
  leafOffsetsY: number[];
  groupWidth: number;
  groupHeight: number;
}

/**
 * Check if a node is a leaf (has no children)
 */
function isLeafNode(nodeId: string, childrenMap: Map<string, string[]>): boolean {
  const children = childrenMap.get(nodeId);
  return !children || children.length === 0;
}

/**
 * Get leaf children of a parent
 */
function getLeafChildren(parentId: string, childrenMap: Map<string, string[]>): string[] {
  const children = childrenMap.get(parentId) || [];
  return children.filter(childId => isLeafNode(childId, childrenMap));
}

/**
 * Get non-leaf children of a parent
 */
function getNonLeafChildren(parentId: string, childrenMap: Map<string, string[]>): string[] {
  const children = childrenMap.get(parentId) || [];
  return children.filter(childId => !isLeafNode(childId, childrenMap));
}

/**
 * Vertical offset of each stacked leaf relative to the first one. Consecutive leaves belonging to
 * different clients get an extra gap so the client grouping returned by the backend reads as
 * distinct blocks instead of one undifferentiated list.
 */
export function calculateLeafOffsetsY(
  leafChildIds: string[],
  nodeDataMap: Map<string, OrgChartNode>
): number[] {
  const offsets: number[] = [];
  let offset = 0;

  for (let i = 0; i < leafChildIds.length; i++) {
    if (i > 0) {
      const previousClient = nodeDataMap.get(leafChildIds[i - 1])?.client;
      const currentClient = nodeDataMap.get(leafChildIds[i])?.client;
      offset += LEAF_STACK_CONFIG.nodeVerticalSpacing;
      if (previousClient !== currentClient) {
        offset += LEAF_STACK_CONFIG.clientGroupGap;
      }
    }
    offsets.push(offset);
  }

  return offsets;
}

/**
 * Calculate the dimensions needed for a leaf group placeholder
 */
function calculateLeafGroupDimensions(leafOffsetsY: number[]): { width: number; height: number } {
  const width = LEAF_STACK_CONFIG.spineOffsetX + 
                LEAF_STACK_CONFIG.branchLength + 
                NODE_WIDTH + 
                LEAF_STACK_CONFIG.leafGroupPadding;
  
  const height = LEAF_STACK_CONFIG.firstLeafOffsetY + 
                 (leafOffsetsY[leafOffsetsY.length - 1] ?? 0) + 
                 NODE_HEIGHT + 
                 LEAF_STACK_CONFIG.leafGroupPadding;
  
  return { width, height };
}

/**
 * Build the children map from org chart data
 */
function buildChildrenMap(treeNodes: OrgChartNode[], map: Map<string, string[]> = new Map()): Map<string, string[]> {
  for (const node of treeNodes) {
    const childIds = (node.children || []).map(c => c.id);
    map.set(node.id, childIds);
    
    if (node.children && node.children.length > 0) {
      buildChildrenMap(node.children, map);
    }
  }
  return map;
}

/**
 * Build a map of node data for quick lookup
 */
function buildNodeDataMap(treeNodes: OrgChartNode[], map: Map<string, OrgChartNode> = new Map()): Map<string, OrgChartNode> {
  for (const node of treeNodes) {
    map.set(node.id, node);
    if (node.children && node.children.length > 0) {
      buildNodeDataMap(node.children, map);
    }
  }
  return map;
}

/**
 * Create a collapsed graph where leaf children groups are replaced with placeholder nodes.
 * This allows dagre to reserve proper space for the leaf stacks.
 */
function createCollapsedGraph(
  childrenMap: Map<string, string[]>,
  nodeDataMap: Map<string, OrgChartNode>
): {
  nodes: { id: string; width: number; height: number; isLeafGroup: boolean }[];
  edges: { source: string; target: string }[];
  leafGroups: Map<string, LeafGroupInfo>;
  leafChildrenToSkip: Set<string>;
} {
  const nodes: { id: string; width: number; height: number; isLeafGroup: boolean }[] = [];
  const edges: { source: string; target: string }[] = [];
  const leafGroups = new Map<string, LeafGroupInfo>();
  const leafChildrenToSkip = new Set<string>();

  // First pass: identify all leaf groups and mark their children to skip
  Array.from(childrenMap.entries()).forEach(([parentId]) => {
    const leafChildren = getLeafChildren(parentId, childrenMap);
    if (leafChildren.length >= LEAF_STACK_CONFIG.minLeafChildrenForStack) {
      const groupId = `leafGroup:${parentId}`;
      const leafOffsetsY = calculateLeafOffsetsY(leafChildren, nodeDataMap);
      const { width, height } = calculateLeafGroupDimensions(leafOffsetsY);
      
      leafGroups.set(groupId, {
        parentId,
        leafChildIds: leafChildren,
        leafOffsetsY,
        groupWidth: width,
        groupHeight: height,
      });
      
      leafChildren.forEach(id => leafChildrenToSkip.add(id));
    }
  });

  // Second pass: create nodes (person nodes + leaf group placeholders)
  Array.from(nodeDataMap.keys()).forEach(nodeId => {
    // Skip leaf children that are part of a leaf group
    if (leafChildrenToSkip.has(nodeId)) {
      return;
    }
    
    nodes.push({
      id: nodeId,
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      isLeafGroup: false,
    });
  });

  // Add leaf group placeholder nodes
  Array.from(leafGroups.entries()).forEach(([groupId, info]) => {
    nodes.push({
      id: groupId,
      width: info.groupWidth,
      height: info.groupHeight,
      isLeafGroup: true,
    });
  });

  // Create edges
  Array.from(childrenMap.entries()).forEach(([parentId]) => {
    const leafChildren = getLeafChildren(parentId, childrenMap);
    const nonLeafChildren = getNonLeafChildren(parentId, childrenMap);
    
    // Add edges to non-leaf children
    for (const childId of nonLeafChildren) {
      edges.push({ source: parentId, target: childId });
    }
    
    // If this parent has a leaf group, add edge to the leaf group placeholder
    if (leafChildren.length >= LEAF_STACK_CONFIG.minLeafChildrenForStack) {
      const groupId = `leafGroup:${parentId}`;
      edges.push({ source: parentId, target: groupId });
    } else {
      // Add edges to individual leaf children (not enough to form a group)
      for (const childId of leafChildren) {
        edges.push({ source: parentId, target: childId });
      }
    }
  });

  return { nodes, edges, leafGroups, leafChildrenToSkip };
}

/**
 * Run dagre layout on the collapsed graph
 */
function runDagreOnCollapsedGraph(
  nodes: { id: string; width: number; height: number; isLeafGroup: boolean }[],
  edges: { source: string; target: string }[]
): Map<string, { x: number; y: number; width: number; height: number }> {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'TB',
    ranker: 'tight-tree',
    nodesep: 40,
    ranksep: 80,
    marginx: 50,
    marginy: 50,
  });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: node.width, height: node.height });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const positions = new Map<string, { x: number; y: number; width: number; height: number }>();
  nodes.forEach((node) => {
    const dagreNode = dagreGraph.node(node.id);
    positions.set(node.id, {
      x: dagreNode.x - node.width / 2,
      y: dagreNode.y - node.height / 2,
      width: node.width,
      height: node.height,
    });
  });

  return positions;
}

/**
 * Expand the collapsed graph back into the full graph with junction nodes
 */
function expandCollapsedGraph(
  positions: Map<string, { x: number; y: number; width: number; height: number }>,
  leafGroups: Map<string, LeafGroupInfo>,
  nodeDataMap: Map<string, OrgChartNode>,
  childrenMap: Map<string, string[]>
): GraphElements {
  const finalNodes: Node<AnyNodeData>[] = [];
  const finalEdges: Edge[] = [];

  // Add all person nodes (non-leaf-group nodes)
  Array.from(positions.entries()).forEach(([nodeId, pos]) => {
    // Skip leaf group placeholders - we'll expand them separately
    if (nodeId.startsWith('leafGroup:')) {
      return;
    }
    
    const nodeData = nodeDataMap.get(nodeId);
    if (!nodeData) return;
    
    finalNodes.push({
      id: nodeId,
      type: 'person',
      position: { x: pos.x, y: pos.y },
      width: NODE_WIDTH,
      height: NODE_HEIGHT,
      data: {
        displayName: nodeData.displayName,
        email: nodeData.email,
        photoUrl: nodeData.photoUrl,
        organizationRole: nodeData.organizationRole,
        client: nodeData.client,
      },
      selectable: true,
      draggable: false,
    });
  });

  // Expand leaf groups into junction nodes + leaf person nodes
  Array.from(leafGroups.entries()).forEach(([groupId, info]) => {
    const groupPos = positions.get(groupId);
    if (!groupPos) return;

    const { parentId, leafChildIds, leafOffsetsY } = info;
    
    // Calculate spine position within the group's reserved space
    const spineX = groupPos.x + LEAF_STACK_CONFIG.spineOffsetX;
    const firstJunctionY = groupPos.y + LEAF_STACK_CONFIG.firstLeafOffsetY;

    // Create junction nodes and leaf person nodes
    for (let i = 0; i < leafChildIds.length; i++) {
      const leafId = leafChildIds[i];
      const junctionId = `junction-${parentId}-leaf-${i}`;
      const junctionY = firstJunctionY + leafOffsetsY[i];

      // Create junction node
      finalNodes.push({
        id: junctionId,
        type: 'junction',
        position: { x: spineX, y: junctionY },
        width: JUNCTION_SIZE,
        height: JUNCTION_SIZE,
        data: {},
        selectable: false,
        draggable: false,
      });

      // Edge from previous junction (or parent) to this junction
      if (i === 0) {
        // First junction connects to parent
        finalEdges.push({
          id: `${parentId}->${junctionId}`,
          source: parentId,
          target: junctionId,
          type: 'step',
          style: { stroke: '#94a3b8', strokeWidth: 2 },
        });
      } else {
        // Connect from previous junction
        const prevJunctionId = `junction-${parentId}-leaf-${i - 1}`;
        finalEdges.push({
          id: `${prevJunctionId}->${junctionId}`,
          source: prevJunctionId,
          sourceHandle: 'bottom',
          target: junctionId,
          type: 'step',
          style: { stroke: '#94a3b8', strokeWidth: 2 },
        });
      }

      // Create leaf person node
      const leafData = nodeDataMap.get(leafId);
      if (leafData) {
        const leafX = spineX + LEAF_STACK_CONFIG.branchLength;
        const leafY = junctionY - NODE_HEIGHT / 2 + JUNCTION_SIZE / 2;
        
        finalNodes.push({
          id: leafId,
          type: 'person',
          position: { x: leafX, y: leafY },
          width: NODE_WIDTH,
          height: NODE_HEIGHT,
          data: {
            displayName: leafData.displayName,
            email: leafData.email,
            photoUrl: leafData.photoUrl,
            organizationRole: leafData.organizationRole,
            client: leafData.client,
          },
          selectable: true,
          draggable: false,
        });
      }

      // Edge from junction to leaf (horizontal branch)
      finalEdges.push({
        id: `${junctionId}->${leafId}`,
        source: junctionId,
        sourceHandle: 'right',
        target: leafId,
        targetHandle: 'left',
        type: 'step',
        style: { stroke: '#94a3b8', strokeWidth: 2 },
      });
    }
  });

  // Add edges for non-leaf children and single leaf children
  Array.from(childrenMap.entries()).forEach(([parentId]) => {
    const leafChildren = getLeafChildren(parentId, childrenMap);
    const nonLeafChildren = getNonLeafChildren(parentId, childrenMap);
    
    // Add edges to non-leaf children
    for (const childId of nonLeafChildren) {
      finalEdges.push({
        id: `${parentId}->${childId}`,
        source: parentId,
        target: childId,
        type: 'step',
        style: { stroke: '#94a3b8', strokeWidth: 2 },
      });
    }
    
    // Add edges to individual leaf children (those not in a group)
    if (leafChildren.length < LEAF_STACK_CONFIG.minLeafChildrenForStack) {
      for (const childId of leafChildren) {
        finalEdges.push({
          id: `${parentId}->${childId}`,
          source: parentId,
          target: childId,
          type: 'step',
          style: { stroke: '#94a3b8', strokeWidth: 2 },
        });
      }
    }
  });

  return { nodes: finalNodes, edges: finalEdges };
}

/**
 * Transform org chart tree data into React Flow nodes and edges
 * 
 * Uses a collapsed graph approach:
 * 1. Build a collapsed graph where leaf children groups become single placeholder nodes
 * 2. Run dagre on the collapsed graph so it reserves proper space for leaf stacks
 * 3. Expand the placeholders back into junction nodes + leaf person nodes
 * 
 * This prevents overlaps because dagre knows about the space needed for leaf stacks.
 */
export function transformOrgChartToGraph(orgChartData: OrgChartNode[]): GraphElements {
  if (!orgChartData || orgChartData.length === 0) {
    return { nodes: [], edges: [] };
  }

  // Build lookup maps
  const childrenMap = buildChildrenMap(orgChartData);
  const nodeDataMap = buildNodeDataMap(orgChartData);

  // Step 1: Create collapsed graph with leaf group placeholders
  const { nodes, edges, leafGroups } = createCollapsedGraph(
    childrenMap,
    nodeDataMap
  );

  // Step 2: Run dagre on collapsed graph
  const positions = runDagreOnCollapsedGraph(nodes, edges);

  // Step 3: Expand back into full graph with junction nodes
  return expandCollapsedGraph(positions, leafGroups, nodeDataMap, childrenMap);
}
